// CRUD pour les transactions raw manuelles.

import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiDelete, apiGet, apiPost } from "../lib/api";

export const Route = createFileRoute("/manual")({
  component: ManualPage,
});

interface ManualTransaction {
  id: number;
  entryDate: string;
  amountCents: number;
  currency: string;
  side: "debit" | "credit";
  label: string;
  accountCode: string | null;
  counterparty: string | null;
  notes: string | null;
  createdAt: string;
}

function ManualPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const { data: rows = [] } = useQuery<ManualTransaction[]>({
    queryKey: ["manual-transactions"],
    queryFn: () => apiGet<ManualTransaction[]>("/api/raw/manual"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDelete(`/api/raw/manual/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["manual-transactions"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Transactions manuelles</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded font-medium"
        >
          + Ajouter une transaction
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Libellé</th>
              <th className="px-3 py-2">Contrepartie</th>
              <th className="px-3 py-2">Compte</th>
              <th className="px-3 py-2">Sens</th>
              <th className="px-3 py-2 text-right">Montant</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100">
                <td className="px-3 py-1.5">{r.entryDate}</td>
                <td className="px-3 py-1.5">{r.label}</td>
                <td className="px-3 py-1.5 text-gray-500">{r.counterparty ?? "—"}</td>
                <td className="px-3 py-1.5 font-mono text-xs">{r.accountCode ?? "auto"}</td>
                <td className="px-3 py-1.5">{r.side === "debit" ? "Débit" : "Crédit"}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {(r.amountCents / 100).toLocaleString("fr-FR", {
                    style: "currency",
                    currency: r.currency || "EUR",
                  })}
                </td>
                <td className="px-3 py-1.5 text-right">
                  <button
                    onClick={() => {
                      if (confirm("Supprimer cette transaction ?")) {
                        deleteMutation.mutate(r.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-800 text-xs"
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-400">
                  Aucune transaction manuelle pour le moment.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && <ManualForm onClose={() => setShowForm(false)} />}
    </div>
  );
}

function ManualForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().split("T")[0];
  const [entryDate, setEntryDate] = useState(today);
  const [amountEuros, setAmountEuros] = useState("0");
  const [side, setSide] = useState<"debit" | "credit">("debit");
  const [label, setLabel] = useState("");
  const [accountCode, setAccountCode] = useState("");
  const [counterparty, setCounterparty] = useState("");
  const [notes, setNotes] = useState("");

  const createMutation = useMutation({
    mutationFn: () =>
      apiPost("/api/raw/manual", {
        entryDate,
        amountCents: Math.round(parseFloat(amountEuros) * 100),
        side,
        label,
        accountCode: accountCode || null,
        counterparty: counterparty || null,
        notes: notes || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["manual-transactions"] });
      queryClient.invalidateQueries({ queryKey: ["raw-status"] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">Nouvelle transaction manuelle</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="space-y-1">
            <span className="text-gray-500">Date</span>
            <input
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1"
            />
          </label>
          <label className="space-y-1">
            <span className="text-gray-500">Sens</span>
            <select
              value={side}
              onChange={(e) => setSide(e.target.value as "debit" | "credit")}
              className="w-full border border-gray-300 rounded px-2 py-1"
            >
              <option value="debit">Débit (sortie)</option>
              <option value="credit">Crédit (entrée)</option>
            </select>
          </label>
          <label className="space-y-1 col-span-2">
            <span className="text-gray-500">Libellé</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="ex: Remboursement client X"
              className="w-full border border-gray-300 rounded px-2 py-1"
            />
          </label>
          <label className="space-y-1">
            <span className="text-gray-500">Montant (€)</span>
            <input
              type="number"
              step="0.01"
              value={amountEuros}
              onChange={(e) => setAmountEuros(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-right"
            />
          </label>
          <label className="space-y-1">
            <span className="text-gray-500">Compte (optionnel)</span>
            <input
              type="text"
              value={accountCode}
              onChange={(e) => setAccountCode(e.target.value)}
              placeholder="ex: 60680000"
              className="w-full border border-gray-300 rounded px-2 py-1 font-mono"
            />
          </label>
          <label className="space-y-1 col-span-2">
            <span className="text-gray-500">Contrepartie (optionnel)</span>
            <input
              type="text"
              value={counterparty}
              onChange={(e) => setCounterparty(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1"
            />
          </label>
          <label className="space-y-1 col-span-2">
            <span className="text-gray-500">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded px-2 py-1"
            />
          </label>
        </div>
        {createMutation.isError && (
          <div className="text-red-600 text-sm">
            Erreur : {(createMutation.error as Error).message}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-700">
            Annuler
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!label || createMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded disabled:opacity-50"
          >
            {createMutation.isPending ? "…" : "Créer"}
          </button>
        </div>
      </div>
    </div>
  );
}
